import clsx from 'clsx';
import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo, useState } from 'react';
import { MarketingTeacher } from '../../../domain/teacher';
import { createArrayOfLength } from '../../../libs/utils';
import {
  ClassOrder,
  FetchSessionsQueryVariables,
  useFetchSessionsMinimalQuery,
  useFetchSessionsQuery,
} from '../../../typescript/generated/codegen';
import SessionCard, { SkeletonSessionCard } from '../sessionCard';
import Filters from './filters/filters';

interface SessionGridProps {
  title: string;
  teacher?: MarketingTeacher;
  onContentUpdate?: any;
}

export const LIMIT = 6;

const SessionGrid: React.FC<SessionGridProps> = ({
  title,
  teacher,
  onContentUpdate,
}) => {
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [classifications, setClassifications] = useState<string[]>([]);

  const [filters, setFilters] = useState<FetchSessionsQueryVariables>({
    classifications: undefined,
    teachers: teacher?.beid ? [teacher.beid] : undefined,
    limit: LIMIT,
    order: ClassOrder.PublishedAtDesc,
    now: useGetNowForSessionsQueries(),
  });

  // fetch data to show total count and available classifications for filters bar
  useFetchSessionsMinimalQuery({
    variables: {
      teachers: teacher?.beid ? [teacher.beid] : undefined,
      now: useGetNowForSessionsQueries(),
    },
    onCompleted: data => {
      setTotal(data?.classCollection?.total);

      // sorted array of unique classifications
      const uniqueClassifications = uniqBy(
        data.classCollection?.items,
        'classification'
      )
        .map(i => i.classification)
        .sort((a, b) => {
          const sortOrder = [
            'Yoga',
            'Pilates',
            'Fitness',
            'Mindfulness',
            'Music',
          ];
          const A = sortOrder.indexOf(a as string);
          const B = sortOrder.indexOf(b as string);

          if (A < B) return -1;
          if (A > B) return 1;
          return 0;
        }) as string[];
      setClassifications(uniqueClassifications);

      // if only one classification exists then preselect it in filters
      if (uniqueClassifications.length === 1)
        handleSetFilters({ classifications: uniqueClassifications });
    },
  });

  // hook for fetching data for the grid
  const { data, loading } = useFetchSessionsQuery({
    variables: filters,
    onCompleted: () => {
      onContentUpdate();
    },
  });

  const handleFetchMore = () => {
    // @TODO implement proper pagination.
    // at this point we are just increasing the fetch limit...
    setFilters({ ...filters, limit: filters.limit + LIMIT });
  };

  const handleSetFilters = (update: { [key: string]: any }) => {
    // reset "pagination" on filter update
    setFilters({ ...filters, limit: LIMIT, ...update });
  };

  const hasMore = data?.classCollection
    ? filters.limit < data?.classCollection?.total
    : false;

  return (
    <section className="pt-24px pb-48px sm:py-48px content">
      <div className="text-3xl py-24px sm:py-32px font-family-bold">
        {title}{' '}
        {typeof total !== 'undefined' && (
          <span className="text-sm font-family-semibold">({total})</span>
        )}
      </div>

      <Filters
        filters={filters}
        setFilters={handleSetFilters}
        classifications={classifications}
      />

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-16px sm:gap-24px mt-32px">
          {createArrayOfLength(filters.limit).map((_, i) => (
            <SkeletonSessionCard key={i} />
          ))}
        </div>
      )}

      {data?.classCollection?.items && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-16px sm:gap-24px mt-32px">
            {data.classCollection.items.map(session => (
              <SessionCard
                key={`sessionGrid-${session.beid}`}
                session={session}
                showTeacher={false}
              />
            ))}
          </div>

          {hasMore && (
            <div className="text-center pt-32px sm:pt-48px">
              <span
                className={clsx(
                  'underline font-family-bold',
                  'cursor-pointer hover:text-accent-hover'
                )}
                onClick={() => handleFetchMore()}
              >
                Show more
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default SessionGrid;

export const useGetNowForSessionsQueries = () =>
  useMemo(() => dayjs().toISOString(), []);
